import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Upload, FileText, CheckCircle, ArrowLeft, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';

export default function ApplyJob() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const jobId = searchParams.get('job');
  const jobType = searchParams.get('type'); // 'zoho' or 'local'
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    address: '',
    phone: '',
    email: '',
    resume: null
  });
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    if (jobId) {
      fetchJob();
    }
  }, [jobId, jobType]);

  const fetchJob = async () => {
    try {
      let response;
      if (jobType === 'zoho') {
        response = await api.get(`/zoho/jobs`);
        const jobs = response.data;
        const foundJob = jobs.find(j => j.id === jobId);
        if (foundJob) setJob(foundJob);
      } else {
        response = await api.get(`/jobs/${jobId}`);
        setJob(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch job:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a PDF or DOC/DOCX file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setFormData({ ...formData, resume: file });
      setFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!formData.resume) {
      toast.error('Please upload your CV/Resume');
      return;
    }

    setLoading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        const payload = {
          full_name: formData.full_name,
          address: formData.address,
          phone: formData.phone,
          email: formData.email,
          resume_base64: base64,
          resume_filename: fileName,
          job_id: jobId || null,
          job_type: jobType || null,
          job_title: job?.title || 'General Application'
        };

        try {
          await api.post('/apply-job', payload);
          setSubmitted(true);
          toast.success('Application submitted successfully!');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to submit application');
        } finally {
          setLoading(false);
        }
      };
      reader.readAsDataURL(formData.resume);
    } catch (error) {
      toast.error('Failed to process your application');
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4" data-testid="apply-success">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-zinc-100 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-3">Application Submitted!</h1>
            <p className="text-zinc-600 mb-2">
              Thank you for applying{job ? ` for the ${job.title} position` : ''}.
            </p>
            <p className="text-zinc-500 text-sm mb-8">
              We have received your application and will review it shortly. 
              If your qualifications match our requirements, we will contact you.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/jobs">
                <Button variant="outline" className="w-full sm:w-auto">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Browse More Jobs
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4" data-testid="apply-job-page">
      <div className="max-w-2xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/jobs" 
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Jobs
        </Link>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-8 py-6 text-white">
            <h1 className="text-2xl font-bold mb-1">Apply for Position</h1>
            {job ? (
              <p className="text-blue-100">{job.title} • {job.location || 'Location not specified'}</p>
            ) : (
              <p className="text-blue-100">Submit your application</p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="Enter your full name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="h-12"
                data-testid="full-name-input"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12"
                data-testid="email-input"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <Input
                type="tel"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="h-12"
                data-testid="phone-input"
                required
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Address
              </label>
              <Input
                type="text"
                placeholder="Enter your address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="h-12"
                data-testid="address-input"
              />
            </div>

            {/* Resume Upload */}
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Upload CV/Resume <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                  id="resume-upload"
                  data-testid="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  className={`flex items-center justify-center gap-3 h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                    fileName 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-zinc-200 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                >
                  {fileName ? (
                    <div className="text-center">
                      <FileText className="w-10 h-10 text-green-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-green-700">{fileName}</p>
                      <p className="text-xs text-green-600 mt-1">Click to change file</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-10 h-10 text-zinc-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-zinc-600">Click to upload your CV</p>
                      <p className="text-xs text-zinc-400 mt-1">PDF, DOC, DOCX (Max 5MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold rounded-xl"
              disabled={loading}
              data-testid="submit-application-btn"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>

            <p className="text-xs text-zinc-500 text-center">
              By submitting this application, you agree to our privacy policy and terms of service.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
